import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  ArrowLeft,
  Loader2,
  Star,
  AlertTriangle,
  CheckCircle,
  XCircle,
  User,
  MessageSquare,
  BarChart3
} from 'lucide-react';

interface FlaggedInteraction {
  turn_number: number;
  question: string;
  answer: string;
  issue_type: string;
  severity: string;
  comment: string;
}

interface PatientFeedback {
  id: number;
  session_id: string;
  user_id: number;
  realism: number;
  educational_value: number;
  response_appropriateness: number;
  patient_consistency: number;
  clinical_accuracy: number;
  average_rating: number;
  flagged_interactions: FlaggedInteraction[];
  strengths: string | null;
  improvements: string | null;
  additional_comments: string | null;
  evaluator_role: string | null;
  evaluator_specialty: string | null;
  evaluator_experience: string | null;
  created_at: string;
}

const PatientFeedbackViewPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [feedback, setFeedback] = useState<PatientFeedback | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const fetchFeedback = async () => {
    try {
      const response = await apiClient.get(`/api/patient-feedback/${sessionId}`);
      setFeedback(response.data);
    } catch (error: any) {
      console.error('Failed to fetch patient feedback:', error);
      if (error.response?.status === 404) {
        setError('No feedback has been submitted for this session yet.');
      } else {
        setError(error.response?.data?.detail || 'Failed to load patient feedback');
      }
    } finally {
      setLoading(false);
    }
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 8) return 'text-green-600';
    if (rating >= 6) return 'text-blue-600';
    if (rating >= 4) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRatingBadgeColor = (rating: number) => {
    if (rating >= 8) return 'bg-green-500';
    if (rating >= 6) return 'bg-blue-500';
    if (rating >= 4) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Major':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'Moderate':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'Minor':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <div className="bg-primary text-primary-foreground shadow-md">
          <div className="container mx-auto px-4 py-4 flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin')}
              className="text-primary-foreground hover:bg-primary-foreground/10"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-xl font-bold">User Patient Feedback</h1>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8 max-w-2xl">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <div className="mt-4 flex justify-center">
            <Button onClick={() => navigate('/admin')}>Back to Admin Dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  if (!feedback) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-primary text-primary-foreground shadow-md">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin')}
            className="text-primary-foreground hover:bg-primary-foreground/10"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-bold">User Patient Feedback</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 space-y-6 max-w-5xl">
        {/* Summary Card */}
        <Card className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2">Overall Rating</h2>
                <p className="text-white/90">
                  Average across all dimensions
                </p>
                <div className="mt-4 flex items-center gap-4">
                  {feedback.evaluator_role && (
                    <Badge variant="secondary" className="bg-white/20 text-white">
                      <User className="h-3 w-3 mr-1" />
                      {feedback.evaluator_role}
                    </Badge>
                  )}
                  {feedback.evaluator_specialty && (
                    <Badge variant="secondary" className="bg-white/20 text-white">
                      {feedback.evaluator_specialty}
                    </Badge>
                  )}
                  {feedback.evaluator_experience && (
                    <Badge variant="secondary" className="bg-white/20 text-white">
                      {feedback.evaluator_experience} years
                    </Badge>
                  )}
                </div>
              </div>
              <div className="text-center">
                <div className="text-6xl font-bold">{feedback.average_rating.toFixed(1)}</div>
                <div className="text-lg flex items-center justify-center gap-1">
                  <Star className="h-4 w-4 fill-current" />
                  out of 10
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quantitative Ratings */}
        <Card>
          <CardHeader>
            <CardTitle>Quantitative Assessment</CardTitle>
            <p className="text-sm text-muted-foreground">Ratings on 1-10 scale</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <span className="font-medium">Clinical Realism</span>
                <Badge className={`${getRatingBadgeColor(feedback.realism)} text-white text-lg px-4 py-2`}>
                  {feedback.realism}/10
                </Badge>
              </div>

              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <span className="font-medium">Educational Utility</span>
                <Badge className={`${getRatingBadgeColor(feedback.educational_value)} text-white text-lg px-4 py-2`}>
                  {feedback.educational_value}/10
                </Badge>
              </div>

              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <span className="font-medium">Contextual Appropriateness</span>
                <Badge className={`${getRatingBadgeColor(feedback.response_appropriateness)} text-white text-lg px-4 py-2`}>
                  {feedback.response_appropriateness}/10
                </Badge>
              </div>

              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <span className="font-medium">Internal Consistency</span>
                <Badge className={`${getRatingBadgeColor(feedback.patient_consistency)} text-white text-lg px-4 py-2`}>
                  {feedback.patient_consistency}/10
                </Badge>
              </div>

              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <span className="font-medium">Clinical/Factual Accuracy</span>
                <Badge className={`${getRatingBadgeColor(feedback.clinical_accuracy)} text-white text-lg px-4 py-2`}>
                  {feedback.clinical_accuracy}/10
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Flagged Interactions */}
        {feedback.flagged_interactions && feedback.flagged_interactions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                Flagged Interactions ({feedback.flagged_interactions.length})
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Specific AI responses requiring refinement
              </p>
            </CardHeader>
            <CardContent>
              <Accordion type="multiple" className="w-full">
                {feedback.flagged_interactions.map((interaction, index) => (
                  <AccordionItem key={index} value={`interaction-${index}`}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3 w-full">
                        <Badge className={`${getSeverityColor(interaction.severity)} border`}>
                          {interaction.severity}
                        </Badge>
                        <span className="text-sm font-medium">{interaction.issue_type}</span>
                        <span className="text-xs text-muted-foreground ml-auto mr-4">Turn {interaction.turn_number}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-4 pt-2">
                        <div>
                          <p className="text-sm font-semibold mb-1">User Question:</p>
                          <p className="text-sm text-muted-foreground bg-blue-50 p-3 rounded">
                            {interaction.question}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold mb-1">Patient Response:</p>
                          <p className="text-sm text-muted-foreground bg-gray-50 p-3 rounded">
                            {interaction.answer}
                          </p>
                        </div>
                        {interaction.comment && (
                          <div>
                            <p className="text-sm font-semibold mb-1">Feedback Comment:</p>
                            <p className="text-sm text-muted-foreground bg-yellow-50 p-3 rounded border border-yellow-200">
                              {interaction.comment}
                            </p>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        )}

        {/* Qualitative Feedback */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {feedback.strengths && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <CardTitle>Strengths</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{feedback.strengths}</p>
              </CardContent>
            </Card>
          )}

          {feedback.improvements && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-yellow-600" />
                  <CardTitle>Improvement Areas</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{feedback.improvements}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Additional Comments */}
        {feedback.additional_comments && (
          <Card>
            <CardHeader>
              <CardTitle>Additional Comments</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{feedback.additional_comments}</p>
            </CardContent>
          </Card>
        )}

        {/* Metadata */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Submitted: {new Date(feedback.created_at).toLocaleString()}</span>
              <span>Session ID: {feedback.session_id.substring(0, 8)}...</span>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-wrap justify-center gap-4">
          <Button variant="outline" onClick={() => navigate('/admin')}>
            Back to Admin Dashboard
          </Button>
          <Button variant="outline" onClick={() => navigate(`/session/${sessionId}`)}>
            <MessageSquare className="h-4 w-4 mr-2" />
            View Transcript
          </Button>
          <Button variant="outline" onClick={() => navigate(`/evaluation/${sessionId}`)}>
            <BarChart3 className="h-4 w-4 mr-2" />
            View Evaluation
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PatientFeedbackViewPage;
